import { Formik, FormikHelpers, FormikProps } from "formik";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AvisConstants } from "../../../../../data/avisConstants";
import {
  CreationsUnidentifiedRow,
  EventReportWithDataFileRow,
  ItemViewModel,
  ProgramReportInfo,
  ProgramReportRow,
  SubmitProgramReport,
} from "../../../../../generated/data-contracts";
import { useSessionStorage } from "../../../../../hooks/useSessionStorage";
import {
  AvisUtils,
  ErrorUtils,
  IAxiosError,
  LicenseesUrls,
  LicenseesUtils,
  api,
  prepareCollectionForPost,
  nullsToUndefined,
} from "../../../../../utils";

import { useNavigate } from "react-router-dom";
import { AvisForm, AvisLoader } from "../../../../shared/components";
import {
  AvisConfirmButton,
  AvisSaveButton,
} from "../../../../shared/components/buttons";
import { AvisButtonBox } from "../../../../shared/components/form";
import { AvisContext } from "../../../../shared/context/AvisContext";
import { ProgramReportForm } from "./ProgramReportForm";
import { ProgramReportHeader } from "./ProgramReportHeader";
import { CopyProgramReport } from "./modalForms/copyProgramReport/CopyProgramReport";
import { validationClientEventProgramReport } from "./validation";
import { IRowState } from "../../../../shared/components/editableList";
import _ from "lodash";

export function ProgramReportPage() {
  const { res, ctxPortalSettings } = useContext(AvisContext);
  const [error, setError] = useState<IAxiosError>();
  const [firstLoading, setFirstLoading] = useState<boolean>(true);
  const [loadingReportInfo, setLoadingReportInfo] = useState<boolean>(false);
  const [isCopyProgramReportModalOpen, setIsCopyProgramReportModalOpen] =
    useState<boolean>(false);

  const [creationClassesPublicSession, setCreationClassesPublicSession] =
    useSessionStorage(AvisConstants.licenses_creations_filter_classes_key);
  const [creationClassesPublic, setCreationClassesPublic] = useState<
    ItemViewModel[]
  >(creationClassesPublicSession as ItemViewModel[]);
  const [programReport, setProgramReport] = useState<
    ProgramReportInfo | undefined
  >();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);

  const navigate = useNavigate();
  const pageTitleValue = useMemo(() => {
    if (!programReport) return;

    return `${programReport?.licenseNumber}: ${programReport.eventName}`;
  }, [programReport]);

  const urlParams = useMemo(() => LicenseesUtils.getUrlParams(), []);

  const loadProgramReportData = useCallback(
    (formikHelper?: FormikHelpers<SubmitProgramReport>) => {
      if (
        !urlParams.licenseId ||
        !urlParams.licenseInvoicePlanId ||
        !urlParams.licenseClientEventId
      )
        return;

      setLoadingReportInfo(true);

      api
        .classifiersGetUnidentifiedCreationsCreationClasses()
        .then((res) => {
          setCreationClassesPublic(res.data);
          setCreationClassesPublicSession(res.data);
        })
        .catch(setError);

      api
        .licenseesGetProgramReport(
          urlParams.licenseId,
          urlParams.licenseInvoicePlanId,
          urlParams.licenseClientEventId,
        )
        .then((res) => {
          const dataNormalized = nullsToUndefined<ProgramReportInfo>(res.data);
          setProgramReport(dataNormalized);
          if (formikHelper)
            formikHelper.resetForm({ values: getInitValues(dataNormalized) });
        })
        .catch(setError)
        .finally(() => {
          setIsDataLoaded(true);
          setLoadingReportInfo(false);
          setFirstLoading(false);
        });
    },
    [setCreationClassesPublicSession, urlParams],
  );

  const getSubmitValues = useCallback((formikValues: SubmitProgramReport) => {
    const values = _.cloneDeep(formikValues);
    prepareCollectionForPost(values, "programReportRows");
    prepareCollectionForPost(values, "creationsUnidentifiedRows");
    prepareCollectionForPost(values, "dataFileRows");

    return values;
  }, []);

  useEffect(() => {
    if (isSubmitting || loadingReportInfo || isDataLoaded) return;

    loadProgramReportData();
  }, [loadingReportInfo, isSubmitting, isDataLoaded, loadProgramReportData]);

  const handleSubmit = (
    formikValues: SubmitProgramReport,
    formikHelpers: FormikHelpers<SubmitProgramReport>,
  ) => {
    if (!programReport) return;
    setIsSubmitting(true);

    const urlParams = LicenseesUtils.getUrlParams();
    if (
      !urlParams.licenseId ||
      !urlParams.licenseInvoicePlanId ||
      !urlParams.licenseClientEventId
    )
      return;

    const values = getSubmitValues(formikValues);

    api
      .licenseesPostProgramReport(
        urlParams.licenseId,
        urlParams.licenseInvoicePlanId,
        urlParams.licenseClientEventId,
        values,
      )
      .then(() => {
        loadProgramReportData(formikHelpers);
        ErrorUtils.showSuccessfulMessage(
          res("message_Success"),
          res("licensees_LicenseReports_PP_notification_Save"),
        );
      })
      .catch((err) => ErrorUtils.handleFormValidation(err, formikHelpers))
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const getInitValues = (programReportIn: ProgramReportInfo | undefined) => {
    return {
      eventLocation: programReportIn?.eventLocation
        ? programReportIn.eventLocation
        : "",
      dataFileRows:
        programReportIn?.dataFileRows as IRowState<EventReportWithDataFileRow>[],
      programReportRows:
        programReportIn?.programReportRows as IRowState<ProgramReportRow>[],
      creationsUnidentifiedRows:
        programReportIn?.creationsUnidentifiedRows as IRowState<CreationsUnidentifiedRow>[],
      portal_LicenseProgramReportId:
        programReportIn?.portal_LicenseProgramReportId,
    };
  };

  const renderActionButtons = useCallback(
    (editAllowed: boolean, formikProps: FormikProps<SubmitProgramReport>) => {
      const values = getSubmitValues(formikProps.values);
      const emptyProgramReport =
        values.creationsUnidentifiedRows?.length === 0 &&
        values.programReportRows?.length === 0 &&
        values.dataFileRows?.length === 0;

      const emptyProgramReportAndLocation =
        emptyProgramReport && !values.eventLocation;

      const anyRowsChanged =
        !_.isEqualWith(
          values.programReportRows,
          programReport?.programReportRows,
        ) ||
        !_.isEqualWith(
          values.creationsUnidentifiedRows,
          programReport?.creationsUnidentifiedRows,
        ) ||
        !_.isEqualWith(values.dataFileRows, programReport?.dataFileRows);

      return (
        <AvisButtonBox className="program-report-button-box">
          {editAllowed && (
            <>
              <AvisSaveButton
                onClick={formikProps.submitForm}
                disabled={isSubmitting || emptyProgramReportAndLocation}
              />
              <AvisSaveButton
                label={res("button_SaveAndClose")}
                onClick={async () => {
                  const errors = await formikProps.validateForm();
                  if (Object.keys(errors).length > 0) return;

                  formikProps.submitForm().then(() => {
                    const canGoBack = AvisUtils.canGoHistoryBack();
                    if (canGoBack) {
                      navigate(-1);
                    } else {
                      navigate(
                        LicenseesUtils.getUrlWithParams(
                          LicenseesUrls.licenseeLicenseViewReport,
                          urlParams,
                        ),
                        { replace: true },
                      );
                    }
                  });
                }}
                disabled={isSubmitting || emptyProgramReportAndLocation}
              />
            </>
          )}
          <AvisConfirmButton
            className="copy-program-report"
            label={res("button_Copy")}
            onClick={() => setIsCopyProgramReportModalOpen(true)}
            disabled={isSubmitting || emptyProgramReport || anyRowsChanged}
          />
        </AvisButtonBox>
      );
    },
    [getSubmitValues, programReport, isSubmitting, res, navigate, urlParams],
  );

  const renderForm = useCallback(
    (formikProps: FormikProps<SubmitProgramReport>) => {
      if (!programReport) return null;
      const editAllowed = AvisUtils.isEditablePortalReportStatusTypeInfo(
        programReport?.portalReportStatusType,
      );
      return (
        <>
          <ProgramReportHeader data={programReport} bag={formikProps} />
          <ProgramReportForm
            bag={formikProps}
            creationClassesPublic={creationClassesPublic ?? []}
            disabled={!editAllowed}
            loading={isSubmitting || loadingReportInfo}
          />
          {renderActionButtons(editAllowed, formikProps)}
        </>
      );
    },
    [
      programReport,
      creationClassesPublic,
      isSubmitting,
      loadingReportInfo,
      renderActionButtons,
    ],
  );

  return (
    <AvisForm
      pageTitle={pageTitleValue}
      error={error}
      loading={loadingReportInfo && firstLoading}
      backLink={{
        title: res("label_Back"),
        rewrite: true,
        forceNavigateToUrlParams: [
          AvisConstants.licenses_reports_pp_copy_param,
        ],
        url: LicenseesUtils.getUrlWithParams(
          LicenseesUrls.licenseeLicenseViewReport,
          {
            ...urlParams,
            //specific navigation after copying pp (fix #153760)
            isCopy:
              location.search.indexOf(
                `${AvisConstants.licenses_reports_pp_copy_param}=1`,
              ) > -1,
          },
        ),
      }}
    >
      {programReport && (
        <>
          <Formik<SubmitProgramReport>
            initialValues={getInitValues(programReport)}
            onSubmit={handleSubmit}
            validationSchema={validationClientEventProgramReport(
              res,
              ctxPortalSettings?.uploadableDocumentTypes,
              ctxPortalSettings?.unsupportedFilenameSymbols ?? [],
              ctxPortalSettings?.maxFileUploadSize ?? 0,
            )}
            validateOnBlur={false}
            validateOnChange={false}
          >
            {renderForm}
          </Formik>
          {isSubmitting && <AvisLoader />}
          {programReport && isCopyProgramReportModalOpen && (
            <CopyProgramReport
              isModalOpen={isCopyProgramReportModalOpen}
              setIsModalOpen={setIsCopyProgramReportModalOpen}
              sourceProgramReport={programReport}
              setError={setError}
            />
          )}
        </>
      )}
    </AvisForm>
  );
}
